import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Chip,
  Stack,
  Paper,
  Grid,
  Alert,
  InputAdornment,
  IconButton
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';

const SkillsTabV2 = ({ skills = { technical: [], soft: [], languages: [], tools: [] }, onUpdate, onDirtyChange }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [localSkills, setLocalSkills] = useState({ technical: [], soft: [], languages: [], tools: [] });
  const [isDirty, setIsDirty] = useState(false);
  const [newSkill, setNewSkill] = useState({ technical: '', soft: '', languages: '', tools: '' });

  useEffect(() => {
    setLocalSkills({ ...skills });
  }, [skills]);

  useEffect(() => {
    if (onDirtyChange) {
      onDirtyChange(isDirty);
    }
  }, [isDirty, onDirtyChange]);

  const handleEdit = () => setIsEditing(true);

  const handleSave = () => {
    onUpdate(localSkills);
    setIsEditing(false);
    setIsDirty(false);
  };

  const handleCancel = () => {
    setLocalSkills({ ...skills });
    setIsEditing(false);
    setIsDirty(false);
    setNewSkill({ technical: '', soft: '', languages: '', tools: '' });
  };

  const handleAddSkill = (category) => {
    const skill = newSkill[category].trim();
    if (skill) {
      setLocalSkills({
        ...localSkills,
        [category]: [...localSkills[category], skill]
      });
      setNewSkill({ ...newSkill, [category]: '' });
      setIsDirty(true);
    }
  };

  const handleDeleteSkill = (category, index) => {
    setLocalSkills({
      ...localSkills,
      [category]: localSkills[category].filter((_, i) => i !== index)
    });
    setIsDirty(true);
  };

  const handleKeyPress = (category) => (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleAddSkill(category);
    }
  };

  const SkillCategory = ({ title, category, placeholder, readOnly }) => (
    <Paper sx={{ p: 3, mb: 2 }}>
      <Typography variant="h6" gutterBottom>
        {title}
      </Typography>

      {!readOnly && (
        <TextField
          fullWidth
          size="small"
          placeholder={placeholder}
          value={newSkill[category]}
          onChange={(e) => setNewSkill({ ...newSkill, [category]: e.target.value })}
          onKeyPress={handleKeyPress(category)}
          sx={{ mb: 2 }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => handleAddSkill(category)}
                  disabled={!newSkill[category].trim()}
                >
                  <AddIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      )}

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        {localSkills[category].map((skill, index) => (
          <Chip
            key={index}
            label={skill}
            onDelete={!readOnly ? () => handleDeleteSkill(category, index) : undefined}
            color="primary"
            variant={readOnly ? "filled" : "outlined"}
            sx={{ mb: 1 }}
          />
        ))}
      </Stack>

      {localSkills[category].length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
          No {title.toLowerCase()} added yet
        </Typography>
      )}
    </Paper>
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">Skills</Typography>
        {!isEditing ? (
          <Button variant="outlined" startIcon={<EditIcon />} onClick={handleEdit}>
            Edit
          </Button>
        ) : (
          <Stack direction="row" spacing={2}>
            <Button variant="outlined" startIcon={<CancelIcon />} onClick={handleCancel}>
              Cancel
            </Button>
            <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave} disabled={!isDirty}>
              Save Changes
            </Button>
          </Stack>
        )}
      </Box>

      {isDirty && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          You have unsaved changes. Click "Save Changes" to commit them.
        </Alert>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <SkillCategory
            title="Technical Skills"
            category="technical"
            placeholder="e.g., Python, React, AWS..."
            readOnly={!isEditing}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <SkillCategory
            title="Soft Skills"
            category="soft"
            placeholder="e.g., Leadership, Communication..."
            readOnly={!isEditing}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <SkillCategory
            title="Languages"
            category="languages"
            placeholder="e.g., English, Spanish..."
            readOnly={!isEditing}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <SkillCategory
            title="Tools"
            category="tools"
            placeholder="e.g., Git, Docker, Jupyter..."
            readOnly={!isEditing}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default SkillsTabV2;
